// Settings page — CYGE tab pattern with icon triggers
import { useEffect, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  CheckCircle2, AlertCircle, Loader2, Wifi, WifiOff,
  Building2, Bot, Shield, RefreshCw, Users, UserCircle,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useSettings } from '@/hooks/useSettings'
import { useOrgSettings } from '@/hooks/useOrgSettings'
import {
  fetchAgents, testBackendConnection, getBackendUrl,
  type Agent,
} from '@/modules/agents/agent-client'
import { ExpertsSettingsTab } from '@/components/settings/ExpertsSettingsTab'
import { CustomersSettingsTab } from '@/components/settings/CustomersSettingsTab'
import { UsersSettingsTab } from '@/components/settings/UsersSettingsTab'
import { AgentTestDialog } from '@/components/settings/AgentTestDialog'

const ALL_D365_VERSIONS = [
  '10.0.37', '10.0.38', '10.0.39', '10.0.40',
  '10.0.41', '10.0.42', '10.0.43', '10.0.44',
  '10.0.45', '10.0.46',
]
const ALL_PRODUCTS = [
  { value: 'finance', label: 'Finance' },
  { value: 'supply_chain', label: 'Supply Chain Management' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'human_resources', label: 'Human Resources' },
  { value: 'project_operations', label: 'Project Operations' },
]
const ALL_DEPLOYMENT_TYPES = [
  { value: 'cloud', label: 'Cloud (Microsoft-managed)' },
  { value: 'on_premises', label: 'On-Premises' },
  { value: 'cloud_hosted_dev', label: 'Cloud-Hosted Dev/Test' },
]

const FEATURE_AGENTS: { key: string; label: string; description: string }[] = [
  { key: 'issue_recorder', label: 'Issue Recorder', description: 'Conversational issue capture agent' },
  { key: 'kb_search', label: 'KB Search', description: 'Knowledge base semantic search agent' },
  { key: 'kb_manager', label: 'KB Manager', description: 'Knowledge base operations agent' },
  { key: 'innovation_orchestrator', label: 'Innovation Orchestrator', description: 'Workflow coordination and multi-agent orchestration' },
  { key: 'finance_expert', label: 'Finance Expert', description: 'D365 Finance module specialist (GL, AP, AR, Cash, Fixed Assets)' },
  { key: 'scm_expert', label: 'Supply Chain Expert', description: 'D365 SCM specialist (Inventory, Warehouse, Procurement, Production)' },
  { key: 'technical_expert', label: 'Technical Expert', description: 'D365 technical specialist (X++, integrations, customizations)' },
  { key: 'infrastructure_expert', label: 'Infrastructure Expert', description: 'D365 infrastructure specialist (LCS, Azure, deployment, database)' },
  { key: 'integration_expert', label: 'Integration Expert', description: 'D365 integration specialist (OData, Dual-write, Power Platform)' },
  { key: 'data_migration_expert', label: 'Data Migration Expert', description: 'D365 data migration specialist (DMF, BYOD, data packages)' },
  { key: 'security_expert', label: 'Security & Access Expert', description: 'D365 security specialist (Roles, duties, privileges, SoD)' },
  { key: 'configuration_expert', label: 'Configuration Expert', description: 'D365 configuration specialist (Feature management, parameters, workflows)' },
  { key: 'customer_profiler', label: 'Customer Profiler', description: 'Analyzes customer documentation and generates comprehensive environment profiles' },
  { key: 'm365_expert', label: 'M365 Integration Expert', description: 'Microsoft 365 specialist (Excel add-in, Power BI, Power Platform, Office 365)' },
  { key: 'resolution_writer', label: 'Resolution Writer', description: 'Generates step-by-step user instructions from search results for PDF/DOCX export' },
  { key: 'pii_redaction', label: 'PII Redaction', description: 'Detects and redacts personal data (GDPR) using local Ollama — data never leaves Railway' },
  { key: 'doc_embedder', label: 'Document Embedder', description: 'Generates 768-dim embeddings for document chunks (OpenAI text-embedding-3-small)' },
]

export function Settings(): JSX.Element {
  const { user: _user, organization: _organization } = useAuth()
  const { settings, saving, saveSettings } = useSettings()

  // Backend connection state
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [backendStatus, setBackendStatus] = useState<boolean | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [applicationId, setApplicationId] = useState('')

  // Draft settings (local until save)
  const [drafts, setDrafts] = useState(settings)
  const [dirty, setDirty] = useState(false)
  const [_saveSuccess, setSaveSuccess] = useState(false)

  // Feature agent assignments (organization-level, stored in DB)
  const orgSettings = useOrgSettings()
  const [featureDrafts, setFeatureDrafts] = useState<Record<string, string>>({})
  const [featureDirty, setFeatureDirty] = useState(false)

  // Agent test dialog
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [testDialogOpen, setTestDialogOpen] = useState(false)

  useEffect(() => {
    loadData()
    setApiKey(localStorage.getItem('organization_api_key') || '')
    setApplicationId(localStorage.getItem('application_id') || import.meta.env.VITE_APPLICATION_ID || 'd365-ai-assistant')
  }, [])

  // Load feature assignments from org settings (DB-backed)
  useEffect(() => {
    if (!orgSettings.isLoading) {
      console.log('[Settings] Loaded feature assignments from org settings:', orgSettings.featureAssignments)
      setFeatureDrafts(orgSettings.featureAssignments)
    }
  }, [orgSettings.isLoading, orgSettings.featureAssignments])

  useEffect(() => {
    setDrafts(settings)
    setDirty(false)
  }, [settings])

  const loadData = async () => {
    try {
      const agentsData = await fetchAgents().catch(() => [])
      setAgents(agentsData)
    } catch (err) {
      console.error('Failed to load settings data:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateDraft = (key: keyof typeof drafts, value: unknown) => {
    setDrafts((prev) => ({ ...prev, [key]: value }))
    setDirty(true)
    setSaveSuccess(false)
  }

  const handleTestConnection = async () => {
    setTesting(true)
    setBackendStatus(null)
    try {
      const ok = await testBackendConnection()
      setBackendStatus(ok)
      if (ok) await loadData()
    } catch {
      setBackendStatus(false)
    } finally {
      setTesting(false)
    }
  }

  const handleSaveApiKey = () => {
    if (apiKey.trim()) {
      localStorage.setItem('organization_api_key', apiKey.trim())
    } else {
      localStorage.removeItem('organization_api_key')
    }
  }

  const handleSaveApplicationId = () => {
    if (applicationId.trim()) {
      localStorage.setItem('application_id', applicationId.trim())
    } else {
      localStorage.removeItem('application_id')
    }
  }

  const handleSaveSettings = async () => {
    try {
      await saveSettings(drafts)
      setDirty(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch {
      // error is already in useSettings
    }
  }

  const handleSaveFeatureAssignments = async () => {
    console.log('[Settings] Saving feature assignments to DB:', featureDrafts)
    await orgSettings.saveFeatureAssignments(featureDrafts)
    setFeatureDirty(false)
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 3000)
  }

  const handleClearFeatureAssignments = async () => {
    console.log('[Settings] Clearing feature assignments')
    await orgSettings.clearFeatureAssignments()
    setFeatureDrafts({})
    setFeatureDirty(false)
  }

  const handleClearAllAssignments = async () => {
    console.log('[Settings] Clearing ALL assignments')
    await orgSettings.clearFeatureAssignments()
    setFeatureDrafts({})
    setFeatureDirty(false)
    alert('All agent assignments cleared!')
  }

  const handleOpenTestDialog = (agent: Agent) => {
    setSelectedAgent(agent)
    setTestDialogOpen(true)
  }

  // Debug logging
  useEffect(() => {
    console.log('[Settings] Agents loaded:', agents.length, agents.map(a => ({
      assistant_id: a.assistant_id,
      agent_id: a.agent_id,
      name: a.name,
      provider: a.provider,
    })))
    console.log('[Settings] Feature drafts:', JSON.stringify(featureDrafts, null, 2))
  }, [agents, featureDrafts])

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure your Kaycoo platform connection and agent assignments
        </p>
      </div>

      <Tabs defaultValue="platform" className="space-y-6">
        <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-grid">
          <TabsTrigger value="platform" className="gap-2">
            <Wifi className="h-4 w-4" />
            <span className="hidden sm:inline">Platform</span>
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Customers</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <UserCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="d365" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">D365 F&O</span>
          </TabsTrigger>
          <TabsTrigger value="agents" className="gap-2">
            <Bot className="h-4 w-4" />
            <span className="hidden sm:inline">Agents</span>
          </TabsTrigger>
          <TabsTrigger value="experts" className="gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Experts</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
        </TabsList>

        {/* Platform Connection */}
        <TabsContent value="platform">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5" />
                Kaycoo Platform Connection
              </CardTitle>
              <CardDescription>
                Connect to the Kaycoo Agentic Platform to manage AI agents for this application. All provider configuration is handled on the platform.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="application-id">Application ID</Label>
                <div className="flex gap-2">
                  <Input
                    id="application-id"
                    placeholder="d365-ai-assistant"
                    value={applicationId}
                    onChange={(e) => setApplicationId(e.target.value)}
                  />
                  <Button onClick={handleSaveApplicationId} variant="outline">Save</Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Must match the application_id assigned to your agents in the Kaycoo platform. Only agents with this ID will appear in dropdowns.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="api-key">Organization API Key</Label>
                <div className="flex gap-2">
                  <Input
                    id="api-key"
                    type="password"
                    placeholder="ck_..."
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  <Button onClick={handleSaveApiKey} variant="outline">Save</Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Your organization API key for Kaycoo platform authentication (format: ck_*)
                </p>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Backend URL:</span>
                <span className="font-mono text-xs">{getBackendUrl()}</span>
              </div>

              <Button
                onClick={handleTestConnection}
                disabled={testing}
                variant="outline"
                className="w-full"
              >
                {testing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Testing Connection...</>
                ) : (
                  <><Wifi className="mr-2 h-4 w-4" />Test Connection & Refresh Agents</>
                )}
              </Button>

              {backendStatus !== null && (
                <Alert variant={backendStatus ? 'default' : 'destructive'}>
                  {backendStatus ? <CheckCircle2 className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
                  <AlertDescription>
                    {backendStatus
                      ? `Connected to Kaycoo Platform. ${agents.length} agent(s) available.`
                      : 'Cannot reach Kaycoo Platform. Verify URL and API key are correct.'}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleClearAllAssignments}
                variant="destructive"
                className="w-full"
              >
                Clear All Agent Assignments
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customers */}
        <TabsContent value="customers">
          <CustomersSettingsTab />
        </TabsContent>

        {/* Users */}
        <TabsContent value="users">
          <UsersSettingsTab />
        </TabsContent>

        {/* D365 F&O Systems */}
        <TabsContent value="d365">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Supported D365 F&O Systems
              </CardTitle>
              <CardDescription>
                Configure which D365 F&O versions, products, and deployment types your customers use.
                These populate dropdowns in issue creation and guide the AI agent during resolution.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Supported Versions</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_D365_VERSIONS.map((v) => {
                    const active = drafts.supportedVersions.includes(v)
                    return (
                      <Button
                        key={v}
                        size="sm"
                        variant={active ? 'default' : 'outline'}
                        className={active ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                        onClick={() => {
                          const next = active
                            ? drafts.supportedVersions.filter((x) => x !== v)
                            : [...drafts.supportedVersions, v]
                          updateDraft('supportedVersions', next)
                        }}
                      >
                        {v}
                      </Button>
                    )
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Select D365 F&O versions your customers run. Agents will filter KB results by these versions.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Supported Products</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_PRODUCTS.map((e) => {
                    const active = drafts.supportedProducts.includes(e.value)
                    return (
                      <Button
                        key={e.value}
                        size="sm"
                        variant={active ? 'default' : 'outline'}
                        className={active ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                        onClick={() => {
                          const next = active
                            ? drafts.supportedProducts.filter((x) => x !== e.value)
                            : [...drafts.supportedProducts, e.value]
                          updateDraft('supportedProducts', next)
                        }}
                      >
                        {e.label}
                      </Button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Supported Deployment Types</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_DEPLOYMENT_TYPES.map((h) => {
                    const active = drafts.supportedDeploymentTypes.includes(h.value)
                    return (
                      <Button
                        key={h.value}
                        size="sm"
                        variant={active ? 'default' : 'outline'}
                        className={active ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                        onClick={() => {
                          const next = active
                            ? drafts.supportedDeploymentTypes.filter((x) => x !== h.value)
                            : [...drafts.supportedDeploymentTypes, h.value]
                          updateDraft('supportedDeploymentTypes', next)
                        }}
                      >
                        {h.label}
                      </Button>
                    )
                  })}
                </div>
              </div>

              <Button
                onClick={handleSaveSettings}
                disabled={!dirty || saving}
                className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
              >
                {saving ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                ) : (
                  <><CheckCircle2 className="mr-2 h-4 w-4" />Save D365 Configuration</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agents — Table + Feature Assignments */}
        <TabsContent value="agents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Platform Agents
              </CardTitle>
              <CardDescription>
                Agents pulled from the Kaycoo platform for this application. Assign agents to features below.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Agents table */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">
                    {agents.length} agent{agents.length !== 1 ? 's' : ''} available
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadData}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Refresh
                  </Button>
                </div>

                {agents.length === 0 && !loading ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No agents found. Go to the Platform tab to configure your connection and Application ID.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="rounded-lg border">
                    {/* Table header */}
                    <div className="grid grid-cols-12 gap-2 border-b bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground">
                      <div className="col-span-3">Name</div>
                      <div className="col-span-2">Provider</div>
                      <div className="col-span-2">Model</div>
                      <div className="col-span-1">Temp</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-2 text-right">Test</div>
                    </div>
                    {/* Table rows */}
                    {agents.map((agent) => (
                      <div
                        key={agent.agent_id || agent.assistant_id}
                        className="grid grid-cols-12 gap-2 border-b px-4 py-3 text-sm last:border-b-0 hover:bg-muted/30"
                      >
                        <div className="col-span-3">
                          <p className="font-medium truncate">{agent.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{agent.description || '—'}</p>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <span className="truncate">{agent.provider}</span>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <span className="font-mono text-xs truncate">{agent.model_id || '—'}</span>
                        </div>
                        <div className="col-span-1 flex items-center">
                          <span className="text-xs">{agent.temperature ?? '—'}</span>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                            {agent.status}
                          </Badge>
                        </div>
                        <div className="col-span-2 flex items-center justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenTestDialog(agent)}
                          >
                            <Bot className="mr-1 h-3.5 w-3.5" />
                            Test
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Feature assignments */}
              {agents.length > 0 && (
                <div className="space-y-4 border-t pt-5">
                  <div>
                    <h4 className="text-sm font-medium">Feature Assignments</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Assign a platform agent to each AI-powered feature ({agents.length} agents available)
                    </p>
                  </div>

                  {FEATURE_AGENTS.map((feat) => {
                    const currentValue = featureDrafts[feat.key]
                    return (
                      <div key={feat.key} className="space-y-1.5">
                        <Label htmlFor={`feat-${feat.key}`}>{feat.label}</Label>
                        <select
                          id={`feat-${feat.key}`}
                          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={currentValue || ''}
                          onChange={(e) => {
                            console.log(`[Settings] Feature ${feat.key} changed to:`, e.target.value)
                            setFeatureDrafts((prev) => {
                              const updated = { ...prev, [feat.key]: e.target.value }
                              console.log('[Settings] Updated featureDrafts:', updated)
                              return updated
                            })
                            setFeatureDirty(true)
                            setSaveSuccess(false)
                          }}
                          disabled={loading || agents.length === 0}
                        >
                          <option value="">-- Select an agent --</option>
                          {agents.map((agent) => (
                            <option key={agent.agent_id || agent.assistant_id} value={agent.agent_id || agent.assistant_id}>
                              {agent.name} ({agent.provider})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">{feat.description}</p>
                      </div>
                    )
                  })}

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveFeatureAssignments}
                      disabled={!featureDirty}
                      className="flex-1 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Save Feature Assignments
                    </Button>
                    <Button
                      onClick={handleClearFeatureAssignments}
                      variant="outline"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expert Profiles */}
        <TabsContent value="experts">
          <ExpertsSettingsTab />
        </TabsContent>

        {/* Security & Compliance */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security & Compliance
              </CardTitle>
              <CardDescription>Data handling and audit configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">AI Execution Logging</p>
                  <p className="text-xs text-muted-foreground">Log all AI agent executions for audit</p>
                </div>
                <Button
                  variant={drafts.enableAiLogging ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateDraft('enableAiLogging', !drafts.enableAiLogging)}
                  className={drafts.enableAiLogging ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                >
                  {drafts.enableAiLogging ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Rate Limiting</p>
                  <p className="text-xs text-muted-foreground">Limit API requests per minute ({drafts.defaultRateLimit}/min)</p>
                </div>
                <Button
                  variant={drafts.enableRateLimiting ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateDraft('enableRateLimiting', !drafts.enableRateLimiting)}
                  className={drafts.enableRateLimiting ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                >
                  {drafts.enableRateLimiting ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Data Residency:</span>
                <span className="font-medium">EU (Frankfurt)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Encryption:</span>
                <span className="font-medium">AES-256 at rest, TLS 1.3 in transit</span>
              </div>
              {dirty && (
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
                >
                  {saving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                  ) : (
                    <><CheckCircle2 className="mr-2 h-4 w-4" />Save All Changes</>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Agent Test Dialog */}
      <AgentTestDialog
        agent={selectedAgent}
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
      />
    </div>
  )
}
